import React, { useState, useCallback, useMemo } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    addEdge,
    Connection,
    Edge,
    Node,
    useNodesState,
    useEdgesState,
    BackgroundVariant,
    ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Save, Loader2 } from 'lucide-react';
import { Workflow, WorkflowNode, WorkflowEdge } from '@/types/agents';
import NodeToolbar from './NodeToolbar';
import CustomNode from './Nodes/CustomNode';
import NodeContextMenu from './NodeContextMenu';

interface WorkflowEditorProps {
    workflow?: Workflow;
    onSave: (workflow: Partial<Workflow>) => void;
    onCancel: () => void;
}

// Initial node types
const initialNodes: Node[] = [
    // {
    //     id: '1',
    //     type: 'input',
    //     data: { label: 'Some Node', some: 'details' },
    //     position: { x: 250, y: 100 },
    // },
    // {
    //     id: '2',
    //     type: 'custom',
    //     data: { label: 'Another Node', description: 'This is another node', options: { allowInput: false, allowOutput: true } },
    //     position: { x: 250, y: 100 },
    // },
    // {
    //     id: '3',
    //     type: 'custom',
    //     data: { label: 'Larger title to test the layout', description: 'This is another node', options: { allowInput: true, allowOutput: true }, category: 'generator', icon: 'Settings2' },
    //     position: { x: 50, y: 200 },
    // },
];

const initialEdges: Edge[] = [];

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({ workflow, onSave, onCancel }) => {
    const [workflowName, setWorkflowName] = useState(workflow?.name || '');
    const [isSaving, setIsSaving] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

    // Initialize nodes and edges from workflow or use defaults
    const initialNodesState = useMemo(() => {
        if (workflow?.nodes && workflow.nodes.length > 0) {
            return workflow.nodes.map((node: WorkflowNode) => ({
                id: node.id,
                type: node.type,
                data: node.data,
                position: node.position,
            })) as Node[];
        }
        return initialNodes;
    }, [workflow]);

    const initialEdgesState = useMemo(() => {
        if (workflow?.edges && workflow.edges.length > 0) {
            return workflow.edges.map((edge: WorkflowEdge) => ({
                id: edge.id,
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                targetHandle: edge.targetHandle,
            })) as Edge[];
        }
        return initialEdges;
    }, [workflow]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodesState);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdgesState);

    const onConnect = useCallback(
        (params: Connection) => {
            setEdges((eds: Edge[]) => addEdge(params, eds));
        },
        [setEdges]
    );

    const onNodeAdd = useCallback(
        (nodeType: string, nodeData: Record<string, any> = {}, position?: { x: number; y: number }) => {
            let nodePosition: { x: number; y: number };

            if (position && reactFlowInstance) {
                // Convert screen coordinates to flow coordinates
                const flowPosition = reactFlowInstance.screenToFlowPosition(position);
                nodePosition = flowPosition;
            } else {
                // Use random position if no position provided
                nodePosition = {
                    x: Math.random() * 500 + 100,
                    y: Math.random() * 400 + 100,
                };
            }

            const newNode: Node = {
                id: `${nodeType}-${Date.now()}`,
                type: nodeType,
                data: { label: nodeData.label || nodeType, ...nodeData },
                position: nodePosition,
            };
            setNodes((nds: Node[]) => [...nds, newNode]);
        },
        [setNodes, reactFlowInstance]
    );

    const onPaneContextMenu = useCallback(
        (event: MouseEvent | React.MouseEvent) => {
            event.preventDefault();
            const clientX = 'clientX' in event ? event.clientX : (event as MouseEvent).clientX;
            const clientY = 'clientY' in event ? event.clientY : (event as MouseEvent).clientY;
            setContextMenuPosition({ x: clientX, y: clientY });
        },
        []
    );

    const handleContextMenuClose = useCallback(() => {
        setContextMenuPosition(null);
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const workflowData: Partial<Workflow> = {
                name: workflowName,
                nodes: nodes.map((node: Node) => ({
                    id: node.id,
                    type: node.type || 'custom', // Ensure type is a string
                    data: node.data,
                    position: node.position,
                })) as WorkflowNode[],
                edges: edges.map((edge: Edge) => ({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle || undefined,
                    targetHandle: edge.targetHandle || undefined,
                })) as WorkflowEdge[],
                ...(workflow && { id: workflow.id }),
            };
            await onSave(workflowData);
        } catch (error) {
            console.error('Error saving workflow:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-background">
            {/* Header */}
            <div
                className="flex items-center justify-between p-4 border-b"
                style={{
                    borderColor: "hsl(var(--muted-foreground))",
                    backgroundColor: "hsl(var(--background))",
                }}
            >
                <div className="flex items-center gap-4 flex-1">
                    <input
                        type="text"
                        value={workflowName}
                        onChange={(e) => setWorkflowName(e.target.value)}
                        placeholder="Workflow Name"
                        className="text-2xl font-bold bg-transparent border-none outline-none focus:ring-2 focus:ring-offset-2 rounded px-2"
                        style={{
                            color: "hsl(var(--foreground))",
                        }}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 rounded-md transition-colors flex items-center"
                        style={{
                            borderColor: "hsl(var(--muted-foreground))",
                            color: "hsl(var(--foreground))",
                            backgroundColor: "hsl(var(--background))",
                            borderWidth: '1px',
                            borderStyle: 'solid',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "hsl(var(--muted-foreground) / 0.1)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "hsl(var(--background))";
                        }}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
                        disabled={isSaving || !workflowName.trim()}
                    >
                        {isSaving ? (
                            <>
                                <Loader2 size={16} className="mr-2 animate-spin" />
                                Saving...
                            </>
                        ) : workflow ? (
                            <>
                                <Save size={16} className="mr-2" />
                                Save
                            </>
                        ) : (
                            <>
                                <Save size={16} className="mr-2" />
                                Create
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-1 overflow-hidden">
                {/* React Flow Canvas */}
                <div className="flex-1 relative">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onInit={setReactFlowInstance}
                        onPaneContextMenu={onPaneContextMenu}
                        fitView
                        style={{
                            backgroundColor: "hsl(var(--background))",
                        }}
                        nodeTypes={{ custom: CustomNode }}
                    >
                        <Background
                            variant={BackgroundVariant.Dots}
                            gap={12}
                            size={1}
                            color="#888"
                        />
                        <Controls
                            style={{
                                backgroundColor: "hsl(var(--background))",
                                borderColor: "hsl(var(--muted-foreground))",
                            }}
                        />
                        <MiniMap
                            style={{
                                backgroundColor: "hsl(var(--background))",
                                borderColor: "hsl(var(--muted-foreground))",
                            }}
                            nodeColor="hsl(var(--primary))"
                        />
                    </ReactFlow>
                </div>

                {/* Right Toolbar */}
                <div
                    className="w-64 border-l overflow-y-auto"
                    style={{
                        borderColor: "hsl(var(--muted-foreground))",
                        backgroundColor: "hsl(var(--background))",
                    }}
                >
                    <NodeToolbar onNodeAdd={onNodeAdd} />
                </div>
            </div>

            {/* Context Menu */}
            <NodeContextMenu
                position={contextMenuPosition}
                onClose={handleContextMenuClose}
                onNodeAdd={onNodeAdd}
            />
        </div>
    );
};

export default WorkflowEditor;

